import { useState, useEffect } from 'react';

const links = [
  { label: "Portfolio", icon: "grid" },
  { label: "Contact", icon: "mail" },
  { label: "LinkedIn", icon: "link" },
];

const LinkInBioAnimation = () => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [phase, setPhase] = useState<'building' | 'highlight' | 'reset'>('building');

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    switch (phase) {
      case 'building':
        if (visibleCount < links.length) {
          timeout = setTimeout(() => setVisibleCount(v => v + 1), 600);
        } else {
          timeout = setTimeout(() => {
            setActiveIndex(0);
            setPhase('highlight');
          }, 800);
        }
        break;

      case 'highlight':
        if (activeIndex < links.length - 1) {
          timeout = setTimeout(() => setActiveIndex(i => i + 1), 1200);
        } else {
          timeout = setTimeout(() => setPhase('reset'), 2000);
        }
        break;

      case 'reset':
        timeout = setTimeout(() => {
          setVisibleCount(0);
          setActiveIndex(-1);
          setPhase('building');
        }, 800);
        break;
    }

    return () => clearTimeout(timeout);
  }, [phase, visibleCount, activeIndex]);

  const MiniIcon = ({ type, className }: { type: string; className: string }) => {
    switch (type) {
      case 'grid':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <rect width="7" height="7" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" />
            <rect width="7" height="7" x="3" y="14" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" />
          </svg>
        );
      case 'mail':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        );
      case 'link':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="mt-6 mb-2">
      <div className="rounded-lg border border-primary/20 bg-primary/[0.03] overflow-hidden max-w-[300px] shadow-sm">
        {/* Title bar */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-primary/10 bg-primary/[0.04]">
          <span className="w-[7px] h-[7px] rounded-full bg-primary/20" />
          <span className="w-[7px] h-[7px] rounded-full bg-primary/15" />
          <span className="w-[7px] h-[7px] rounded-full bg-primary/10" />
          <span className="text-[10px] text-primary/40 ml-2 select-none tracking-wide">my-links.io</span>
        </div>

        {/* Content */}
        <div className="px-4 h-[82px] flex flex-col justify-center gap-[6px]">
          {links.map((link, i) => {
            const isVisible = i < visibleCount;
            const isActive = i === activeIndex;
            return (
              <div
                key={i}
                className={`flex items-center gap-2 px-2.5 py-[5px] rounded border transition-all duration-500 ${
                  isVisible
                    ? isActive
                      ? 'border-primary/30 bg-primary/10 opacity-100 translate-y-0'
                      : 'border-primary/10 bg-transparent opacity-100 translate-y-0'
                    : 'border-transparent bg-transparent opacity-0 translate-y-2'
                }`}
              >
                <MiniIcon
                  type={link.icon}
                  className={`w-[11px] h-[11px] transition-colors duration-500 ${
                    isActive ? 'text-primary/60' : 'text-muted-foreground/30'
                  }`}
                />
                <span className={`text-[10px] select-none transition-colors duration-500 ${
                  isActive ? 'text-primary/60' : 'text-muted-foreground/40'
                }`}>
                  {link.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LinkInBioAnimation;
